import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Chip, getContrastTextColor } from '@structural-climbing/ui'
import { tint } from './branding/brandTheme'

export interface JourneyBranding {
  name: string
  image_url: string | null
  cover_url: string | null
  primary_color: string | null
  secondary_color: string | null
}

export interface JourneyPlan {
  name: string
  price: number
  currency: string
  plan_type: 'day_pass' | 'entry_based' | 'duration_based'
  max_entries: number | null
  duration_days: number
}

interface Props {
  branding: JourneyBranding
  plans: JourneyPlan[]
}

/** The member app's price format (apps/member/src/features/payments/price.ts). */
function formatPrice(price: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(price)
  } catch {
    return `${price} ${currency}`.trim()
  }
}

/**
 * A picture of a decorative button. `as="div"` keeps it out of the tab order,
 * since nothing in a mock-up does anything.
 */
function MockButton({ bg, color, children }: { bg: string; color: string; children: ReactNode }) {
  return (
    <Button
      as="div"
      tabIndex={-1}
      disableRipple
      size="sm"
      className="pointer-events-none w-full text-[11px] font-semibold"
      style={{ backgroundColor: bg, color }}
    >
      {children}
    </Button>
  )
}

/** A phone outline. Fixed height so the three line up side by side. */
function Phone({ children, background }: { children: ReactNode; background: string }) {
  return (
    <div
      className="relative mx-auto h-[400px] w-full max-w-[220px] overflow-hidden rounded-[2rem] border-[6px] border-foreground/85 shadow-large"
      style={{ backgroundColor: background }}
      aria-hidden="true"
    >
      <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-12 -translate-x-1/2 rounded-full bg-foreground/85" />
      {children}
    </div>
  )
}

/**
 * The three screens a member goes through to renew online, drawn in this gym's
 * colours with its own plans, so the owner sees what their members will see.
 *
 * Like the branding screens (branding/MemberScreens), these are hand-drawn copies of the member app
 * (CheckInHero's expired state, PayPlanSheet, then Stripe Checkout). When those
 * screens change, these change with them, or the tab sells something members
 * no longer get.
 */
export function MemberPaymentJourney({ branding, plans }: Props) {
  const { t, i18n } = useTranslation()
  const accent = branding.primary_color ?? '#BD4627'
  const accentText = getContrastTextColor(accent)
  const tintSource = branding.secondary_color ?? accent
  const wash = tint(tintSource, 97, 0.45)
  const veil = tint(tintSource, 94, 0.4)
  const line = tint(tintSource, 91, 0.35)

  const price = (p: JourneyPlan) => formatPrice(p.price, p.currency, i18n.language)
  const shown = plans.slice(0, 3)
  // One plan carries through all three screens: the one that ran out is the
  // one picked again and paid for. A monthly plan is what most members renew,
  // so it leads when there is one.
  const chosen = shown.find((p) => p.plan_type === 'duration_based') ?? shown[0]

  const initials = branding.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  function planDetail(p: JourneyPlan): string {
    if (p.plan_type === 'day_pass') return t('payments.mockPlanDayPass')
    if (p.max_entries !== null) return t('payments.mockPlanEntries', { count: p.max_entries })
    return t('payments.mockPlanDays', { count: p.duration_days })
  }

  const logo = (size: string) => (
    <div
      className={`${size} flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-content2 text-[10px] font-bold`}
      style={{ borderColor: accent }}
    >
      {branding.image_url ? (
        <img src={branding.image_url} alt="" className="h-full w-full object-cover" />
      ) : (
        <p className="text-foreground/60">{initials}</p>
      )}
    </div>
  )

  // Screen 1 doubles as the backdrop behind the sheet in screen 2.
  const expiredScreen = (
    <>
      <div
        className="h-20 w-full overflow-hidden"
        style={branding.cover_url ? undefined : { backgroundColor: accent }}
      >
        {branding.cover_url && (
          <img src={branding.cover_url} alt="" className="h-full w-full object-cover" />
        )}
      </div>
      <div className="flex items-start gap-2 px-3">
        <div className="-mt-5">{logo('h-11 w-11')}</div>
        <p className="truncate pt-1 text-xs font-bold">{branding.name}</p>
      </div>
      <div
        className="mx-3 mt-3 overflow-hidden rounded-2xl bg-white"
        style={{ border: `1px solid ${line}` }}
      >
        <div
          className="flex items-start justify-between gap-2 px-3 py-2.5"
          style={{ backgroundColor: veil }}
        >
          <div className="min-w-0">
            <p className="text-[8px] font-semibold uppercase tracking-[0.08em] text-foreground/40">
              {t('preview.membership')}
            </p>
            <p className="truncate text-[11px] font-semibold">{chosen?.name}</p>
          </div>
          <Chip size="sm" variant="flat" className="h-5 text-[9px]">
            {t('payments.mockExpired')}
          </Chip>
        </div>
        <div className="flex flex-col gap-2.5 px-3 py-3">
          <p className="text-[10px] leading-snug text-foreground/50">
            {t('payments.mockRenewIntro')}
          </p>
          <div
            className="rounded-medium ring-2 ring-offset-2"
            style={{ ['--tw-ring-color' as string]: `${accent}66` }}
          >
            <MockButton bg={accent} color={accentText}>
              {t('payments.mockBuy')}
            </MockButton>
          </div>
        </div>
      </div>
    </>
  )

  const steps = [
    {
      title: t('payments.step1Title'),
      body: t('payments.step1Body'),
      screen: (
        <Phone background={wash}>
          {expiredScreen}
          {/* The feed under the plan card, so the screen reads as the app. */}
          <div className="mx-3 mt-4">
            <p className="mb-2 px-1 text-[8px] font-semibold uppercase tracking-[0.08em] text-foreground/40">
              {t('preview.upcoming')}
            </p>
            <div
              className="flex flex-col gap-2.5 rounded-2xl bg-white p-3"
              style={{ border: `1px solid ${line}` }}
            >
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-md" style={{ backgroundColor: veil }} />
                  <div className="flex flex-col gap-1">
                    <div className="h-1.5 w-20 rounded bg-foreground/10" />
                    <div className="h-1.5 w-12 rounded bg-foreground/10" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Phone>
      ),
    },
    {
      title: t('payments.step2Title'),
      body: t('payments.step2Body'),
      screen: (
        <Phone background={wash}>
          {expiredScreen}
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2.5 rounded-t-3xl bg-white px-3.5 pb-4 pt-2">
            <div className="mx-auto h-1 w-8 rounded-full bg-foreground/15" />
            <div>
              <p className="text-xs font-semibold">{t('payments.mockSheetTitle')}</p>
              <p className="mt-0.5 text-[9px] leading-snug text-foreground/55">
                {t('payments.mockSheetIntro')}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {shown.map((p, i) => (
                <div key={`${p.name}-${i}`} className="flex items-start gap-2">
                  <div
                    className="mt-0.5 flex h-3 w-3 shrink-0 items-center justify-center rounded-full border-2"
                    style={{ borderColor: p === chosen ? accent : 'rgba(0,0,0,0.25)' }}
                  >
                    {p === chosen && (
                      <div
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: accent }}
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] leading-tight">
                      {p.name} <b className="ml-1">{price(p)}</b>
                    </p>
                    <p className="text-[9px] text-foreground/45">{planDetail(p)}</p>
                  </div>
                </div>
              ))}
            </div>
            {chosen && (
              <MockButton bg={accent} color={accentText}>
                {t('payments.mockPay', { price: price(chosen) })}
              </MockButton>
            )}
            <p className="text-[8px] text-foreground/40">{t('payments.mockStripeNote')}</p>
          </div>
        </Phone>
      ),
    },
    {
      title: t('payments.step3Title'),
      body: t('payments.step3Body'),
      screen: (
        <Phone background="#ffffff">
          <div className="flex h-full flex-col gap-3 px-3.5 pb-3 pt-6">
            <div className="flex items-center gap-2">
              {logo('h-6 w-6')}
              <p className="truncate text-[10px] font-semibold">{branding.name}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-foreground/50">{chosen?.name}</p>
              <p className="text-lg font-bold tabular-nums">{chosen ? price(chosen) : ''}</p>
            </div>
            <MockButton bg="#000000" color="#ffffff">
              {t('payments.mockWallet')}
            </MockButton>
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-foreground/10" />
              <p className="text-[8px] text-foreground/40">{t('payments.mockOrCard')}</p>
              <div className="h-px flex-1 bg-foreground/10" />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="h-6 rounded-md border border-foreground/15 px-2 text-[9px] leading-6 text-foreground/35">
                email@example.com
              </div>
              <div className="h-6 rounded-md border border-foreground/15 px-2 text-[9px] leading-6 text-foreground/35">
                1234 1234 1234 1234
              </div>
              <div className="flex gap-1.5">
                <div className="h-6 flex-1 rounded-md border border-foreground/15 px-2 text-[9px] leading-6 text-foreground/35">
                  MM / YY
                </div>
                <div className="h-6 flex-1 rounded-md border border-foreground/15 px-2 text-[9px] leading-6 text-foreground/35">
                  CVC
                </div>
              </div>
            </div>
            <MockButton bg="#0074D4" color="#ffffff">
              {t('payments.mockStripePay')}
            </MockButton>
            <p className="mt-auto text-center text-[8px] text-foreground/35">
              {t('payments.mockPoweredBy')}
            </p>
          </div>
        </Phone>
      ),
    },
  ]

  return (
    // A swipeable row on a phone, where three stacked phones would be a very
    // long scroll; three columns from sm up.
    <div className="-mx-3 flex snap-x snap-mandatory gap-4 overflow-x-auto px-3 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:pb-0">
      {steps.map((step, i) => (
        <div
          key={step.title}
          className="flex w-[78%] shrink-0 snap-center flex-col gap-4 sm:w-auto"
        >
          {step.screen}
          <div className="flex items-start gap-3">
            <Chip
              size="sm"
              radius="full"
              className="h-7 w-7 min-w-7 shrink-0 justify-center px-0 font-bold"
              classNames={{ content: 'px-0 text-center' }}
              style={{ backgroundColor: accent, color: accentText }}
            >
              {i + 1}
            </Chip>
            <div>
              <p className="text-sm font-semibold">{step.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-foreground/60">{step.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
